import { toast } from 'ravenjs/lib/Toaster';

import { NOMINATE_FOR_VPA, PREPPED_ANSWER_DETAILS, PREPPED_ANSWERS } from 'constants/tickets';
import { MODALS } from 'constants/modals';
import { contentLinkIsValidResource } from 'utils/casecave';
import _some from 'lodash/some';

export function usePreppedAnswerActionsMenuHandlers({
    closeModal,
    data,
    handlers,
    history,
    openModal,
    permissions,
    preppedAnswerId,
    refetchData,
    useReadOnlyMode,
}) {
    function handleOnClickComplete() {
        openModal(MODALS.CONFIRM, {
            description: PREPPED_ANSWERS.DETAILS.MODALS.COMPLETE_MODAL.DESCRIPTION,
            actions: [
                {
                    text: PREPPED_ANSWERS.DETAILS.MODALS.COMPLETE_MODAL.BUTTONS.CANCEL,
                    color: 'secondary',
                    onClick: () => {
                        closeModal(MODALS.CONFIRM);
                    },
                },
                {
                    text: PREPPED_ANSWERS.DETAILS.MODALS.COMPLETE_MODAL.BUTTONS.COMPLETE,
                    color: 'primary',
                    onClick: async () => {
                        const success = await handlers.onClickComplete(data.vpaRequestId);
                        if (success) {
                            toast.success(PREPPED_ANSWERS.DETAILS.MODALS.COMPLETE_MODAL.SUCCESS);
                            await refetchData();
                        } else {
                            toast.error(PREPPED_ANSWERS.DETAILS.MODALS.COMPLETE_MODAL.ERROR);
                        }
                        closeModal(MODALS.CONFIRM);
                    },
                },
            ],
        });
    }

    function handleOnClickDecline() {
        openModal(MODALS.CONFIRM, {
            description: PREPPED_ANSWERS.DETAILS.MODALS.DECLINE_MODAL.DESCRIPTION,
            actions: [
                {
                    text: PREPPED_ANSWERS.DETAILS.MODALS.DECLINE_MODAL.BUTTONS.CANCEL,
                    color: 'secondary',
                    onClick: () => {
                        closeModal(MODALS.CONFIRM);
                    },
                },
                {
                    text: PREPPED_ANSWERS.DETAILS.MODALS.DECLINE_MODAL.BUTTONS.DECLINE,
                    color: 'primary',
                    onClick: async () => {
                        const success = await handlers.onClickDecline(data.vpaRequestId);
                        if (success) {
                            toast.success(PREPPED_ANSWERS.DETAILS.MODALS.DECLINE_MODAL.SUCCESS);
                            await refetchData();
                        } else {
                            toast.error(PREPPED_ANSWERS.DETAILS.MODALS.DECLINE_MODAL.ERROR);
                        }
                        closeModal(MODALS.CONFIRM);
                    },
                },
            ],
        });
    }

    function handleOnClickDowngrade() {
        openModal(MODALS.CONFIRM, {
            description: PREPPED_ANSWERS.DETAILS.MODALS.DOWNGRADE_MODAL.DESCRIPTION,
            actions: [
                {
                    text: PREPPED_ANSWERS.DETAILS.MODALS.DOWNGRADE_MODAL.BUTTONS.CANCEL,
                    color: 'secondary',
                    onClick: () => {
                        closeModal(MODALS.CONFIRM);
                    },
                },
                {
                    text: PREPPED_ANSWERS.DETAILS.MODALS.DOWNGRADE_MODAL.BUTTONS.DOWNGRADE,
                    color: 'primary',
                    onClick: async () => {
                        const success = await handlers.onClickDowngrade(preppedAnswerId);
                        if (success) {
                            toast.success(PREPPED_ANSWERS.DETAILS.MODALS.DOWNGRADE_MODAL.SUCCESS);
                            await refetchData();
                        } else {
                            toast.error(PREPPED_ANSWERS.DETAILS.MODALS.DOWNGRADE_MODAL.ERROR);
                        }
                        closeModal(MODALS.CONFIRM);
                    },
                },
            ],
        });
    }

    function handleOnClickEdit() {
        if (permissions.edit) {
            history.push(`/case-cave/prepped-answers/${preppedAnswerId}/edit`);
        }
    }

    function handleOnClickSuggestUpdate(description) {
        openModal(MODALS.COMMON, {
            title: PREPPED_ANSWER_DETAILS.MENU_BAR_ITEMS.ACTIONS.ITEMS.SUGGEST_UPDATE,
            hasCloseIcon: true,
            isComponent: true,
            hideIcon: true,
            hideActions: true,
            description,
        });
    }

    function handleOnClickDelete() {
        if (permissions.delete) {
            openModal(MODALS.CONFIRM, {
                description: PREPPED_ANSWERS.DETAILS.MODALS.DELETE_MODAL.DESCRIPTION,
                actions: [
                    {
                        text: PREPPED_ANSWERS.DETAILS.MODALS.DELETE_MODAL.BUTTONS.CANCEL,
                        color: 'secondary',
                        onClick: () => {
                            closeModal(MODALS.CONFIRM);
                        },
                    },
                    {
                        text: PREPPED_ANSWERS.DETAILS.MODALS.DELETE_MODAL.BUTTONS.DELETE,
                        color: 'primary',
                        onClick: async () => {
                            const success = await handlers.onClickDelete(preppedAnswerId);
                            if (success) {
                                toast.success(PREPPED_ANSWERS.DETAILS.MODALS.DELETE_MODAL.SUCCESS);
                                await refetchData();
                            } else {
                                toast.error(PREPPED_ANSWERS.DETAILS.MODALS.DELETE_MODAL.ERROR);
                            }
                            closeModal(MODALS.CONFIRM);
                        },
                    },
                ],
            });
        }
    }

    function handleOnClickChangeAuthor() {
        const id = data?.preppedAnswer?.id;

        openModal(MODALS.CHANGE_AUTHOR, {
            preppedAnswerIds: [id],
            paTransferCallback: () => {
                refetchData();
                closeModal(MODALS.CHANGE_AUTHOR);
            },
        });
    }

    function handleOnClickClone() {
        if (permissions.clone) {
            history.push(`/case-cave/prepped-answers/${preppedAnswerId}/clone`);
        }
    }

    function handleOnClickNominateVPA(description) {
        if (data.isVPA) {
            openModal(MODALS.COMMON, {
                title: PREPPED_ANSWER_DETAILS.MENU_BAR_ITEMS.ACTIONS.ITEMS.SUGGEST_UPDATE,
                hasCloseIcon: true,
                isComponent: true,
                hideIcon: true,
                hideActions: true,
                description,
            });
        } else {
            openModal(MODALS.NOMINATE_FOR_VPA, {
                hasCloseIcon: true,
                hideIcon: true,
                commentProps: {
                    isMissingResources:
                        data.resourceCount < 1 &&
                        !_some(data.contentAttributes, ({ content_type }) =>
                            contentLinkIsValidResource(content_type)
                        ),
                    preppedAnswerId,
                    onSubmit: async payload => {
                        const success = await handlers.onClickNominate(payload);
                        if (success) {
                            toast.success(NOMINATE_FOR_VPA.SUCCESS);
                            await refetchData();
                        } else {
                            toast.error(NOMINATE_FOR_VPA.ERROR.CREATE);
                        }
                        closeModal(MODALS.NOMINATE_FOR_VPA);
                    },
                },
                title: PREPPED_ANSWERS.DETAILS.MODALS.NOMINATE_MODAL.TITLE,
            });
        }
    }

    function handleOnClickViewComments() {
        if (permissions.viewComment) {
            openModal(MODALS.COMMENTS, {
                hasCloseIcon: true,
                hideIcon: true,
                commentProps: {
                    preppedAnswerId,
                    preppedAnswerCurrentState: data.preppedAnswer.current_state,
                    useReadOnlyMode: useReadOnlyMode || data.isDeletedPreppedAnswer,
                },
                title: PREPPED_ANSWERS.DETAILS.MODALS.COMMENTS_MODAL.TITLE,
                onClickCloseIcon: async () => {
                    await refetchData();
                    closeModal(MODALS.COMMENTS);
                },
            });
        }
    }

    function handleOnClickViewResource() {
        if (permissions.viewResource) {
            openModal(MODALS.RESOURCES, {
                hasCloseIcon: true,
                hideIcon: true,
                onClickCloseIcon: async () => {
                    closeModal(MODALS.RESOURCES);
                },
                resourcesProps: {
                    preppedAnswerAuthorId: data.preppedAnswer.user_id,
                    preppedAnswerId,
                    preppedAnswerIsVPA: data.isVPA,
                    preppedAnswerCurrentState: data.preppedAnswer.current_state,
                    useReadOnlyMode: true,
                },
                title: PREPPED_ANSWERS.DETAILS.MODALS.RESOURCES_MODAL.TITLE,
            });
        }
    }

    function handleOnClickHistory(description) {
        if (permissions.history) {
            openModal(MODALS.COMMON, {
                title: PREPPED_ANSWERS.HISTORY.TITLE,
                description,
                isComponent: true,
                hasCloseIcon: true,
                hideIcon: true,
                onCloseIcon: () => {
                    closeModal(MODALS.COMMON);
                },
                actions: [
                    {
                        text: PREPPED_ANSWERS.HISTORY.BUTTONS.CLOSE,
                        color: 'secondary',
                        onClick: () => {
                            closeModal(MODALS.COMMON);
                        },
                    },
                ],
            });
        }
    }

    return {
        handlers: {
            onClickClone: handleOnClickClone,
            onClickComplete: handleOnClickComplete,
            onClickDecline: handleOnClickDecline,
            onClickDelete: handleOnClickDelete,
            onClickChangeAuthor: handleOnClickChangeAuthor,
            onClickDowngrade: handleOnClickDowngrade,
            onClickEdit: handleOnClickEdit,
            onClickHistory: handleOnClickHistory,
            onClickNominateVPA: handleOnClickNominateVPA,
            onClickSuggestUpdate: handleOnClickSuggestUpdate,
            onClickViewComments: handleOnClickViewComments,
            onClickViewResource: handleOnClickViewResource,
        },
    };
}
